import { useGate, useUnit } from 'effector-react';
import { Autocomplete, type AutocompleteProps, TextField } from '@mui/material';
import {
  ControlWrapper,
  getDefaultInputPropsAutocomplete,
  getAutocompleteOnChange,
  getAutocompleteValue,
  withControl,
} from '@unicorn/control';
import { EventTypesControlGate, eventTypesQuery } from './model';
import type { EventType } from './types';

export const EventTypesControl = withControl<
  Partial<AutocompleteProps<EventType, boolean, boolean, boolean>>
>(
  ({
    label,
    value,
    setValue,
    error,
    isError,
    required,
    readOnly,
    disabled,
    helperText,
    multiple,
    clearable,
    placeholder,
    ...props
  }) => {
    useGate(EventTypesControlGate);
    const { data, pending } = useUnit(eventTypesQuery);

    const options = Array.isArray(data) ? data : [];

    const handleChange = (
      _: unknown,
      newValue: string | EventType | (string | EventType)[] | null
    ) => getAutocompleteOnChange(newValue, setValue, multiple);

    return (
      <ControlWrapper
        label={label}
        disabled={disabled}
        readOnly={readOnly}
        required={required}
        isError={isError}
        error={error}
        helperText={helperText}
      >
        <Autocomplete
          value={getAutocompleteValue(options, value, multiple)}
          onChange={handleChange}
          options={options}
          multiple={multiple}
          loading={pending}
          disableClearable={!clearable}
          renderInput={(params) => {
            const slotProps = {
              input: getDefaultInputPropsAutocomplete({
                params,
                readOnly,
                disabled,
                pending,
              }),
            };

            return (
              <TextField
                {...params}
                error={isError}
                placeholder={
                  multiple && Array.isArray(value) && value.length > 0 ? '' : placeholder
                }
                slotProps={slotProps}
              />
            );
          }}
          readOnly={readOnly}
          disabled={disabled}
          {...props}
        />
      </ControlWrapper>
    );
  }
);
